import { useState, useEffect } from "react";
import { Autocomplete, Chip, TextField } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import type { SyntheticEvent } from "react";

import { type FormData } from "./types";

// ─── Styles ───────────────────────────────────────────────────────────────────

const AUTOCOMPLETE_SX = {
  "& .MuiOutlinedInput-root": {
    minHeight: 44,
    height: "auto",
    borderRadius: "8px",
    fontFamily: "Inter, sans-serif",
    fontSize: 14,
    padding: "6px 12px",
    flexWrap: "wrap",
    gap: "4px",
    "& fieldset": { borderColor: "#DFDFDF" },
    "&:hover fieldset": { borderColor: "#DFDFDF" },
    "&.Mui-focused fieldset": { borderColor: "#8139D0" },
  },
  "& .MuiInputBase-input": {
    fontFamily: "Inter, sans-serif",
    fontSize: 14,
    color: "#000000",
    padding: "4px 0 !important",
    minWidth: "80px",
    "&::placeholder": { color: "#999999", opacity: 1 },
  },
  "& .MuiAutocomplete-endAdornment": {
    top: "50%",
    transform: "translateY(-50%)",
  },
};

const CHIP_SX = {
  backgroundColor: "#EDEDED",
  borderRadius: "100px",
  height: 26,
  "& .MuiChip-label": {
    paddingLeft: "10px",
    paddingRight: "4px",
    fontFamily: "Inter, sans-serif",
    fontSize: 12,
    fontWeight: 500,
    color: "#000000",
  },
  "& .MuiChip-deleteIcon": {
    fontSize: "14px !important",
    color: "#7b7b7b !important",
    margin: "0 8px 0 2px !important",
    "&:hover": { color: "#000000 !important" },
  },
};

// Dropdown paper styles: Inter font, no bold, matching app palette
const DROPDOWN_PAPER_SX = {
  borderRadius: "8px",
  boxShadow: "0px 4px 16px rgba(0,0,0,0.10)",
  border: "1px solid #DFDFDF",
  "& .MuiAutocomplete-listbox": {
    padding: "4px",
    "& .MuiAutocomplete-option": {
      fontFamily: "Inter, sans-serif",
      fontSize: 14,
      fontWeight: 400,
      color: "#000000",
      borderRadius: "4px",
      padding: "8px 12px",
      "&.Mui-selected": { backgroundColor: "#f5f2fc" },
      "&.Mui-focused": { backgroundColor: "#f5f2fc" },
    },
  },
};

// ─── ChipField ────────────────────────────────────────────────────────────────
// Controlled inputValue prevents freeSolo from adding partial text on blur/escape.

function ChipField({
  label,
  helperText,
  options,
  value,
  onChange,
  error,
}: {
  label: string;
  helperText: string;
  options: string[];
  value: string[];
  // eslint-disable-next-line no-unused-vars
  onChange: (_v: string[]) => void;
  error?: string;
}) {
  // No controlled inputValue — letting MUI manage it prevents arrow-key / desync bugs.
  // autoSelect=false stops blur from committing typed text as a chip.
  // filterSelectedOptions hides already-added items from the dropdown.
  const handleChange = (_: SyntheticEvent, newValue: (string | { inputValue?: string })[]) => {
    const strings = newValue.map((v) => {
      const raw = typeof v === "string" ? v : (v.inputValue ?? "");
      // Normalize to canonical option when a case-insensitive match exists
      const match = options.find((opt) => opt.toLowerCase() === raw.toLowerCase());
      return match ?? raw;
    });
    // Deduplicate case-insensitively (freeSolo guard)
    const unique = strings.filter((v, i, arr) => arr.findIndex((x) => x.toLowerCase() === v.toLowerCase()) === i);
    onChange(unique);
  };

  return (
    <div className="rah-form-card__field-group" style={{ gap: 6 }}>
      <p className="rah-form-card__field-label">{label}</p>
      <Autocomplete
        multiple
        freeSolo
        fullWidth
        filterSelectedOptions
        autoSelect={false}
        options={options}
        value={value}
        onChange={handleChange}
        renderTags={(val, getTagProps) =>
          val.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            const chipLabel = typeof option === "string" ? option : (option.inputValue ?? "");
            return <Chip key={key} label={chipLabel} {...tagProps} sx={CHIP_SX} deleteIcon={<CloseIcon />} />;
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={value.length === 0 ? "Type to search or add" : ""}
            error={!!error}
            sx={AUTOCOMPLETE_SX}
          />
        )}
        slotProps={{ paper: { sx: DROPDOWN_PAPER_SX } }}
      />
      <p className="rah-form-card__helper-text">{helperText}</p>
      {error && <p className="rah-field-error">{error}</p>}
    </div>
  );
}

// ─── Step 2: Capabilities ─────────────────────────────────────────────────────

function StepCapabilities({
  data,
  onChange,
  onBack,
  onContinue,
  editMode = false,
  toolOptions = [],
  languageOptions = [],
}: {
  data: FormData;
  // eslint-disable-next-line no-unused-vars
  onChange: (_patch: Partial<FormData>) => void;
  onBack: () => void;
  onContinue: () => void;
  editMode?: boolean;
  toolOptions?: string[];
  languageOptions?: string[];
}) {
  const [errors, setErrors] = useState<{ tools?: string; languages?: string }>({});

  // Auto-seed English when the step first renders with no languages selected
  useEffect(() => {
    if (data.languages.length === 0) {
      onChange({ languages: ["English"] });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleContinueClick = () => {
    const newErrors: { tools?: string; languages?: string } = {};
    if (data.tools.length === 0) newErrors.tools = "Please add at least one tool";
    if (data.languages.length === 0) newErrors.languages = "Please add at least one language";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    onContinue();
  };

  return (
    <div className="rah-form-card">
      {/* Card header */}
      <div className="rah-form-card__header">
        <h2 className="rah-form-card__title">What should they be good at?</h2>
        <p className="rah-form-card__subtitle">Add the tools and languages that matter most.</p>
      </div>

      {/* Fields */}
      <div className="rah-form-card__fields">
        <ChipField
          label="Tools"
          helperText="e.g. Epic, Athenahealth, Hootsuite, Canva, HawkSoft"
          options={toolOptions}
          value={data.tools}
          onChange={(v) => {
            onChange({ tools: v });
            if (errors.tools) setErrors((prev) => ({ ...prev, tools: undefined }));
          }}
          error={errors.tools}
        />
        <ChipField
          label="Languages"
          helperText="e.g. English, Spanish, French"
          options={languageOptions}
          value={data.languages}
          onChange={(v) => {
            onChange({ languages: v });
            if (errors.languages) setErrors((prev) => ({ ...prev, languages: undefined }));
          }}
          error={errors.languages}
        />
      </div>

      {/* Footer */}
      <div className="rah-form-card__footer">
        <button type="button" className="rah-form-card__back-btn" onClick={onBack}>
          <ChevronLeftIcon sx={{ fontSize: 22 }} />
          Back
        </button>
        <button type="button" className="rah-form-card__continue-btn" onClick={handleContinueClick}>
          {editMode ? "Save" : "Continue"}
          <ChevronRightIcon sx={{ fontSize: 22 }} />
        </button>
      </div>
    </div>
  );
}

export default StepCapabilities;
